/**
 * DetailedIssueTooltip.js
 * Enhanced modal component that shows row-level details for data quality issues
 * Features: Blur backdrop, centered modal, smooth animations, improved styling
 */

import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../../utils/ThemeSwitcher';
import { X, User, Calendar, Hash, FileText, AlertTriangle, Clock, Copy, XCircle, Type, ChevronDown, Download, Filter } from 'lucide-react';

/**
 * Displays a modal with detailed issue information
 * 
 * @param {Array} issues - Array of detailed issues with { row_index, variable, value, respondent_id, collector_id, interview_date }
 * @param {string} title - Title for the modal (e.g., "10 Outliers")
 * @param {string} issueType - Type of issue: 'outlier', 'duration', 'duplicate', 'missing', 'similar', 'gibberish'
 * @param {number} maxDisplay - Maximum number of rows to display (default: null = show all)
 * @param {React.ReactNode} children - The element to attach the modal trigger to
 * @param {number} totalCount - Optional total count (if backend truncated the issues array)
 */
const DetailedIssueTooltip = ({ 
  issues = [], 
  title = 'Issue Details',
  issueType = 'outlier',
  maxDisplay = null,
  totalCount = null,
  children 
}) => {
  const { isDarkMode } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const modalRef = useRef(null);
  
  // Colors
  const colors = {
    // Backgrounds
    overlay: isDarkMode ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)',
    modalBg: isDarkMode ? '#1a1a1d' : '#FFFFFF',
    headerBg: isDarkMode ? '#141417' : '#F9FAFB',
    rowHover: isDarkMode ? '#222226' : '#F3F4F6',
    rowAlt: isDarkMode ? '#1e1e22' : '#FAFAFA',
    
    // Borders
    border: isDarkMode ? '#2D2E32' : '#E5E7EB',
    borderLight: isDarkMode ? '#252529' : '#F3F4F6',
    
    // Text
    text: isDarkMode ? '#E5E7EB' : '#1F2937',
    textSecondary: isDarkMode ? '#9CA3AF' : '#6B7280',
    textMuted: isDarkMode ? '#6B7280' : '#9CA3AF',
    
    // Accents
    primary: '#3B82F6',
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
    purple: '#8B5CF6',
    
    // Issue type specific
    outlier: { color: '#F59E0B', bg: isDarkMode ? 'rgba(245, 158, 11, 0.15)' : 'rgba(245, 158, 11, 0.1)' },
    duration: { color: '#3B82F6', bg: isDarkMode ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.1)' },
    duplicate: { color: '#8B5CF6', bg: isDarkMode ? 'rgba(139, 92, 246, 0.15)' : 'rgba(139, 92, 246, 0.1)' },
    missing: { color: '#EF4444', bg: isDarkMode ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)' },
    similar: { color: '#8B5CF6', bg: isDarkMode ? 'rgba(139, 92, 246, 0.15)' : 'rgba(139, 92, 246, 0.1)' },
    gibberish: { color: '#F97316', bg: isDarkMode ? 'rgba(249, 115, 22, 0.15)' : 'rgba(249, 115, 22, 0.1)' },
  };
  
  // Get issue type styling
  const getIssueStyle = () => colors[issueType] || colors.outlier;
  
  // Get icon based on issue type
  const getIcon = (size = 18) => {
    const iconColor = getIssueStyle().color;
    switch (issueType) {
      case 'duration': return <Clock size={size} color={iconColor} />;
      case 'duplicate': return <Copy size={size} color={iconColor} />;
      case 'missing': return <XCircle size={size} color={iconColor} />;
      case 'similar': return <Copy size={size} color={iconColor} />;
      case 'gibberish': return <Type size={size} color={iconColor} />;
      default: return <AlertTriangle size={size} color={iconColor} />;
    }
  };
  
  // Get issue type label
  const getIssueTypeLabel = () => {
    switch (issueType) {
      case 'outlier': return 'Outliers';
      case 'duplicate': return 'Duplicates';
      case 'missing': return 'Missing Values';
      case 'duration': return 'Duration Issues';
      case 'similar': return 'Similar Pairs';
      case 'gibberish': return 'Gibberish Responses';
      default: return 'Issues';
    }
  };
  
  // Handle close with animation
  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, 200);
  };
  
  // Handle open
  const handleOpen = () => {
    setIsOpen(true);
  };
  
  // Close on escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  
  // Close on backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };
  
  // Don't show if no issues
  if (!issues || issues.length === 0) {
    return children;
  }
  
  const displayedIssues = maxDisplay ? issues.slice(0, maxDisplay) : issues;
  const realTotal = totalCount || issues.length;
  const issueStyle = getIssueStyle();
  
  return (
    <>
      {/* Trigger element */}
      <div
        onClick={handleOpen}
        style={{ cursor: 'pointer', display: 'inline-block' }}
      >
        {children}
      </div>
      
      {/* Modal */}
      {isOpen && (
        <div
          onClick={handleBackdropClick}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: colors.overlay,
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px',
            animation: isClosing ? 'fadeOut 0.2s ease-out forwards' : 'fadeIn 0.25s ease-out',
          }}
        >
          <div
            ref={modalRef}
            style={{
              width: '100%',
              maxWidth: '800px',
              maxHeight: '80vh',
              backgroundColor: colors.modalBg,
              borderRadius: '20px',
              boxShadow: isDarkMode 
                ? '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255,255,255,0.05)' 
                : '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0,0,0,0.05)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              animation: isClosing ? 'modalSlideOut 0.2s ease-out forwards' : 'modalSlideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
            }}
          >
            {/* Header */}
            <div style={{
              padding: '20px 24px',
              backgroundColor: colors.headerBg,
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                {/* Icon with colored background */}
                <div style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '12px',
                  backgroundColor: issueStyle.bg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  {getIcon(22)}
                </div>
                
                <div>
                  <h2 style={{ 
                    margin: 0,
                    fontSize: '18px',
                    fontWeight: '600',
                    color: colors.text,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}>
                    {title}
                  </h2>
                  <p style={{
                    margin: '4px 0 0 0',
                    fontSize: '13px',
                    color: colors.textSecondary,
                  }}>
                    {getIssueTypeLabel()} • {realTotal.toLocaleString()} total records
                  </p>
                </div>
              </div>
              
              {/* Close button */}
              <button
                onClick={handleClose}
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '10px',
                  border: 'none',
                  backgroundColor: isDarkMode ? '#2D2E32' : '#F3F4F6',
                  color: colors.textSecondary,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = isDarkMode ? '#3D3E42' : '#E5E7EB';
                  e.currentTarget.style.color = colors.text;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = isDarkMode ? '#2D2E32' : '#F3F4F6';
                  e.currentTarget.style.color = colors.textSecondary;
                }}
              >
                <X size={18} />
              </button>
            </div>
            
            {/* Stats Bar */}
            <div style={{
              padding: '12px 24px',
              backgroundColor: isDarkMode ? '#141417' : '#FAFAFA',
              borderBottom: `1px solid ${colors.borderLight}`,
              display: 'flex',
              alignItems: 'center',
              gap: '24px',
              flexShrink: 0,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Hash size={14} color={colors.textMuted} />
                <span style={{ fontSize: '13px', color: colors.textMuted }}>
                  Showing <span style={{ color: colors.text, fontWeight: '600' }}>{displayedIssues.length}</span> of <span style={{ color: colors.text, fontWeight: '600' }}>{realTotal.toLocaleString()}</span>
                </span>
              </div>
            </div>
            
            {/* Table */}
            <div style={{ 
              overflowY: 'auto', 
              flex: 1,
              scrollbarWidth: 'thin',
              scrollbarColor: isDarkMode ? '#3D3E42 transparent' : '#D1D5DB transparent',
            }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse', 
                fontSize: '13px',
              }}>
                <thead>
                  <tr style={{ 
                    backgroundColor: colors.headerBg,
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                  }}>
                    <th style={{ 
                      padding: '12px 16px', 
                      textAlign: 'left', 
                      fontWeight: '600',
                      color: colors.textSecondary,
                      borderBottom: `1px solid ${colors.border}`,
                      fontSize: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Hash size={12} />
                        Row
                      </div>
                    </th>
                    <th style={{ 
                      padding: '12px 16px', 
                      textAlign: 'left', 
                      fontWeight: '600',
                      color: colors.textSecondary,
                      borderBottom: `1px solid ${colors.border}`,
                      fontSize: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <FileText size={12} />
                        Variable
                      </div>
                    </th>
                    <th style={{ 
                      padding: '12px 16px', 
                      textAlign: 'left', 
                      fontWeight: '600',
                      color: colors.textSecondary,
                      borderBottom: `1px solid ${colors.border}`,
                      fontSize: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}>
                      Value
                    </th>
                    <th style={{ 
                      padding: '12px 16px', 
                      textAlign: 'left', 
                      fontWeight: '600',
                      color: colors.textSecondary,
                      borderBottom: `1px solid ${colors.border}`,
                      fontSize: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <User size={12} />
                        Respondent
                      </div>
                    </th>
                    <th style={{ 
                      padding: '12px 16px', 
                      textAlign: 'left', 
                      fontWeight: '600',
                      color: colors.textSecondary,
                      borderBottom: `1px solid ${colors.border}`,
                      fontSize: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}>
                      Collector
                    </th>
                    <th style={{ 
                      padding: '12px 16px', 
                      textAlign: 'left', 
                      fontWeight: '600',
                      color: colors.textSecondary,
                      borderBottom: `1px solid ${colors.border}`,
                      fontSize: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Calendar size={12} />
                        Date
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {displayedIssues.map((issue, idx) => (
                    <tr 
                      key={idx}
                      style={{
                        backgroundColor: idx % 2 === 0 ? 'transparent' : colors.rowAlt,
                        transition: 'background-color 0.15s ease',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.rowHover}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = idx % 2 === 0 ? 'transparent' : colors.rowAlt}
                    >
                      <td style={{ 
                        padding: '12px 16px', 
                        color: colors.textMuted,
                        borderBottom: `1px solid ${colors.borderLight}`,
                        fontFamily: '"SF Mono", "Fira Code", monospace',
                        fontSize: '12px',
                      }}>
                        {issue.row_index ?? '-'}
                      </td>
                      <td style={{ 
                        padding: '12px 16px', 
                        color: colors.text,
                        borderBottom: `1px solid ${colors.borderLight}`,
                        fontWeight: '500',
                      }}>
                        <span style={{
                          backgroundColor: isDarkMode ? '#2D2E32' : '#F3F4F6',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                        }}>
                          {issue.variable || '-'}
                        </span>
                      </td>
                      <td style={{ 
                        padding: '12px 16px', 
                        borderBottom: `1px solid ${colors.borderLight}`,
                        fontFamily: '"SF Mono", "Fira Code", monospace',
                        fontSize: '12px',
                        maxWidth: '120px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {issue.value !== null && issue.value !== undefined ? (
                          <span style={{
                            color: issueStyle.color,
                            backgroundColor: issueStyle.bg,
                            padding: '2px 8px',
                            borderRadius: '4px',
                          }}>
                            {typeof issue.value === 'number' 
                              ? issue.value.toLocaleString(undefined, { maximumFractionDigits: 2 })
                              : String(issue.value)}
                          </span>
                        ) : (
                          <span style={{
                            color: issueType === 'missing' ? colors.error : colors.textMuted,
                            fontStyle: issueType === 'missing' ? 'italic' : 'normal',
                          }}>
                            {issueType === 'missing' ? 'Missing' : '-'}
                          </span>
                        )}
                      </td>
                      <td style={{ 
                        padding: '12px 16px', 
                        color: colors.text,
                        borderBottom: `1px solid ${colors.borderLight}`,
                        fontSize: '13px',
                      }}>
                        {issue.respondent_id || '-'}
                      </td>
                      <td style={{ 
                        padding: '12px 16px', 
                        color: colors.textSecondary,
                        borderBottom: `1px solid ${colors.borderLight}`,
                        fontSize: '13px',
                      }}>
                        {issue.collector_id || '-'}
                      </td>
                      <td style={{ 
                        padding: '12px 16px', 
                        color: colors.textMuted,
                        borderBottom: `1px solid ${colors.borderLight}`,
                        fontSize: '12px',
                      }}>
                        {issue.interview_date || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Footer */}
            <div style={{
              padding: '16px 24px',
              backgroundColor: colors.headerBg,
              borderTop: `1px solid ${colors.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}>
              <span style={{ 
                fontSize: '13px', 
                color: colors.textMuted,
              }}>
                {`${displayedIssues.length} ${getIssueTypeLabel().toLowerCase()} displayed`}
              </span>
              
              <button
                onClick={handleClose}
                style={{
                  padding: '10px 20px',
                  borderRadius: '10px',
                  border: 'none',
                  backgroundColor: issueStyle.color,
                  color: '#FFFFFF',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.15s ease',
                  boxShadow: `0 2px 8px ${issueStyle.color}40`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = `0 4px 12px ${issueStyle.color}50`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = `0 2px 8px ${issueStyle.color}40`;
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes fadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        
        @keyframes modalSlideIn {
          from { 
            opacity: 0; 
            transform: scale(0.95) translateY(10px); 
          }
          to { 
            opacity: 1; 
            transform: scale(1) translateY(0); 
          }
        }
        
        @keyframes modalSlideOut {
          from { 
            opacity: 1; 
            transform: scale(1) translateY(0); 
          }
          to { 
            opacity: 0; 
            transform: scale(0.95) translateY(10px); 
          }
        }
      `}</style>
    </>
  );
};

export default DetailedIssueTooltip;